import * as Sentry from '@sentry/react';

// Track if Sentry has been initialized
let isInitialized = false;

/**
 * Initialize Sentry with the provided configuration
 * This ensures Sentry is only initialized once
 */
export const initializeSentry = (): void => {
  // Skip if already initialized or if DSN is not provided
  if (isInitialized || !import.meta.env.VITE_SENTRY_DSN) {
    return;
  }

  try {
    console.log('Initializing Sentry');
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
        Sentry.feedbackIntegration({
          colorScheme: 'system',
          triggerLabel: 'Feedback',
          triggerAriaLabel: 'Feedback',
          submitButtonLabel: 'Send Feedback',
          messagePlaceholder: 'Did you find any bugs? Do you have any suggestions?',
          formTitle: 'Send Feedback',
        }),
      ],
      // Performance Monitoring
      tracesSampleRate: 1.0, // Capture 100% of transactions in development, adjust in production
      // Session Replay
      replaysSessionSampleRate: 0.1, // Sample rate for session replays (10%)
      replaysOnErrorSampleRate: 1.0, // Sample rate for replays when errors occur (100%)
      environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development',
    });

    isInitialized = true;
    console.log('Sentry initialized successfully');
  } catch (error) {
    console.error('Failed to initialize Sentry:', error);
  }
};

// Export the Sentry instance for direct access when needed
export { Sentry };

/**
 * Capture an exception with Sentry
 * @param error The error to capture
 * @param context Additional context to add to the error
 */
export const captureException = (error: Error, context?: Record<string, any>): void => {
  Sentry.captureException(error, { contexts: context });
};

/**
 * Capture a message with Sentry
 * @param message The message to capture
 * @param level The level of the message
 */
export const captureMessage = (
  message: string,
  level: 'fatal' | 'error' | 'warning' | 'info' | 'debug' = 'info'
): void => {
  Sentry.captureMessage(message, level);
};

/**
 * Set user information for Sentry
 * @param user The user information to set
 */
export const setUser = (
  user: {
    id?: string;
    email?: string;
    username?: string;
    [key: string]: any;
  } | null
): void => {
  Sentry.setUser(user);
};

/**
 * Add breadcrumb for Sentry
 * @param breadcrumb The breadcrumb to add
 */
export const addBreadcrumb = (breadcrumb: Sentry.Breadcrumb): void => {
  Sentry.addBreadcrumb(breadcrumb);
};

/**
 * Set a tag for the current scope
 * @param key The tag key
 * @param value The tag value
 */
export const setTag = (key: string, value: string): void => {
  Sentry.setTag(key, value);
};

/**
 * Wrap a component with Sentry's error boundary
 * @param component The component to wrap
 * @param options Options for the error boundary
 */
export const withErrorBoundary = <P extends object>(
  component: React.ComponentType<P>,
  options: Sentry.ErrorBoundaryProps = {}
): React.ComponentType<P> => {
  return Sentry.withErrorBoundary(component, options);
};
